import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { CLEARANCE_STATUSES } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Plus, Search, Music, MoreHorizontal, Eye, Pencil, ShieldCheck, Gavel, Send, Library, Inbox } from "lucide-react";
import { toast } from "sonner";
import { formatDurationMMSS } from "@/lib/duration";
import { useAuth, canWrite, canFlagAttorneyReview, isAdmin } from "@/lib/auth";
import { PriorityBadge } from "@/components/PriorityTierControl";
import { sortByPriorityThenDate, PRIORITY_TIER_SORT_ORDER, getPriorityTier } from "@/lib/priorityTier";

function moodChips(tags) {
  if (!tags || tags.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const shown = tags.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-foreground/80">{t}</span>
      ))}
      {tags.length > 3 && <span className="text-[10px] text-muted-foreground">+{tags.length - 3}</span>}
    </div>
  );
}

export default function TracksList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const canFlag = canFlagAttorneyReview(user);
  const isAdminUser = isAdmin(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [catalogCode, setCatalogCode] = useState(searchParams.get("catalog_code") || "all");
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Priority Tier — sort by priority order (URGENT → P4 → unassigned) as
  // the default view. Filter is client-side; sort is applied after fetch.
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("priority") || "all");
  const [sortMode, setSortMode] = useState(searchParams.get("sort") || "priority");

  // Load Catalog Code options for the filter dropdown (all statuses so
  // legacy archived references are also filterable).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/catalog-codes");
        if (!cancelled) setCatalogOptions(Array.isArray(r.data) ? r.data : []);
      } catch (_e) {
        // soft-fail — filter just won't populate
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function load(searchQ = q, searchStatus = status, searchCode = catalogCode) {
    setLoading(true);
    try {
      const params = {};
      if (searchQ) params.q = searchQ;
      if (searchStatus && searchStatus !== "all") params.clearance_status = searchStatus;
      const r = await api.get("/tracks", { params });
      let rows = r.data || [];
      // Client-side filter for Catalog Code (covers "Unassigned Legacy" too).
      if (searchCode && searchCode !== "all") {
        if (searchCode === "__unassigned__") {
          rows = rows.filter((t) => !((t.catalog_code || "").trim()));
        } else {
          rows = rows.filter((t) => (t.catalog_code || "").toUpperCase() === searchCode);
        }
      }
      setItems(rows);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    const urlStatus = searchParams.get("status") || "all";
    const urlCode = searchParams.get("catalog_code") || "all";
    setQ(urlQ); setStatus(urlStatus); setCatalogCode(urlCode);
    load(urlQ, urlStatus, urlCode);
    /* eslint-disable-next-line */
  }, [searchParams]);

  function applyFilters() {
    const next = {};
    if (q) next.q = q;
    if (status !== "all") next.status = status;
    if (catalogCode !== "all") next.catalog_code = catalogCode;
    if (priorityFilter !== "all") next.priority = priorityFilter;
    if (sortMode !== "priority") next.sort = sortMode;
    setSearchParams(next);
  }

  // Priority-aware filter + sort applied client-side.
  const visibleItems = useMemo(() => {
    let arr = items;
    if (priorityFilter && priorityFilter !== "all") {
      if (priorityFilter === "__none__") {
        arr = arr.filter((t) => !getPriorityTier(t));
      } else {
        arr = arr.filter((t) => getPriorityTier(t) === priorityFilter);
      }
    }
    if (sortMode === "priority") {
      return sortByPriorityThenDate(arr, "created_at");
    }
    // "recent" — leave in server-provided order (already recent-first)
    return arr;
  }, [items, priorityFilter, sortMode]);

  async function flagAttorney(track) {
    const reason = window.prompt(`Flag '${track.track_title || track.track_id}' for Attorney Review. Reason:`, track.attorney_review_reason || "");
    if (reason === null) return;
    try {
      await api.post(`/tracks/${track.id}/attorney-review`, { attorney_review_required: true, attorney_review_reason: reason });
      toast.success("Flagged for Attorney Review");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div data-testid="tracks-list-page">
      <PageHeader
        title="Catalog / Tracks"
        subtitle="GFMP-controlled catalog intake and rights-readiness records."
        actions={writeAccess ? (
          <div className="flex items-center gap-2">
            {isAdminUser && (
              <Button variant="outline" onClick={() => navigate("/intake")} data-testid="tracks-bulk-intake-button">
                <Inbox className="w-4 h-4 mr-2" />
                Bulk Metadata
              </Button>
            )}
            {isAdminUser && (
              <Button variant="outline" onClick={() => navigate("/intake?mode=audio&dest=catalog")} data-testid="tracks-bulk-audio-intake-button">
                <Music className="w-4 h-4 mr-2" />
                Bulk Audio Intake
              </Button>
            )}
            {isAdminUser && (
              <Button variant="outline" onClick={() => navigate("/catalog-codes")} data-testid="tracks-manage-catalog-codes-button">
                <Library className="w-4 h-4 mr-2" />
                Catalog Code Registry
              </Button>
            )}
            <Button onClick={()=>navigate("/tracks/new")} data-testid="tracks-add-button" style={{background:"hsl(347 72% 22%)"}}><Plus className="w-4 h-4 mr-2"/>Add Track</Button>
          </div>
        ) : (
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="tracks-readonly-badge">Read-only access</span>
        )}
      />
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <Input placeholder="Title, artist, ISRC, catalog code…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="tracks-search-input" onKeyDown={(e)=>{if(e.key==="Enter") applyFilters();}}/>
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="text-xs text-muted-foreground">Clearance status</label>
            <Select value={status} onValueChange={(v)=>{setStatus(v);}}>
              <SelectTrigger data-testid="tracks-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {CLEARANCE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[220px]">
            <label className="text-xs text-muted-foreground">Catalog Code</label>
            <Select value={catalogCode} onValueChange={(v)=>setCatalogCode(v)}>
              <SelectTrigger data-testid="tracks-catalog-code-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Catalog Codes</SelectItem>
                <SelectItem value="__unassigned__">Unassigned Legacy Records</SelectItem>
                {catalogOptions.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    [{c.code}] {c.name}{!c.is_active ? " · archived" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={applyFilters} variant="outline" data-testid="tracks-apply-filters">Apply</Button>
          <div className="min-w-[180px]">
            <label className="text-xs text-muted-foreground">Priority</label>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger data-testid="tracks-priority-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {PRIORITY_TIER_SORT_ORDER.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
                <SelectItem value="__none__">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs text-muted-foreground">Sort</label>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger data-testid="tracks-sort-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">Priority (URGENT first)</SelectItem>
                <SelectItem value="recent">Recently updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Priority</TableHead>
                <TableHead>Track ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Catalog Code</TableHead>
                <TableHead>Catalog Name</TableHead>
                <TableHead>Artist</TableHead>
                <TableHead>Genre</TableHead>
                <TableHead>Mood</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>ISRC</TableHead>
                <TableHead>Master</TableHead>
                <TableHead>Publishing</TableHead>
                <TableHead>Clearance</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Attorney</TableHead>
                <TableHead>Last Reviewed</TableHead>
                <TableHead className="w-[60px] sticky right-0 bg-card"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={17} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={17} className="text-center py-10 text-muted-foreground">
                  <Music className="w-6 h-6 mx-auto mb-2 opacity-60"/>No tracks match. Add a track to begin.
                </TableCell></TableRow>
              )}
              {visibleItems.map(t => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-secondary/40" onClick={()=>navigate(`/tracks/${t.id}`)} data-testid={`tracks-row-${t.id}`}>
                  <TableCell><PriorityBadge tier={t.priority_tier} testid={`tracks-priority-badge-${t.id}`} /></TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{t.track_id}</TableCell>
                  <TableCell className="max-w-[180px] truncate font-medium">{t.track_title || "(untitled)"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.catalog_code ? (
                      t.catalog_code
                    ) : (
                      <span className="text-amber-700 text-[11px] italic font-sans" title="Created before MP4 Catalog Code requirement" data-testid={`tracks-legacy-${t.id}`}>
                        Unassigned Legacy Record
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{t.catalog_name || "—"}</TableCell>
                  <TableCell className="max-w-[140px] truncate">{t.artist || "—"}</TableCell>
                  <TableCell className="text-sm">{t.genre || "—"}</TableCell>
                  <TableCell>{moodChips(t.mood_tags)}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`tracks-duration-${t.id}`}>{formatDurationMMSS(t.duration)}</TableCell>
                  <TableCell className="font-mono text-xs">{t.isrc || "—"}</TableCell>
                  <TableCell className="text-xs">{t.master_status || "—"}</TableCell>
                  <TableCell className="text-xs">{t.publishing_status || "—"}</TableCell>
                  <TableCell><StatusBadge status={t.clearance_status} /></TableCell>
                  <TableCell className="text-sm">{t.risk_level}</TableCell>
                  <TableCell>
                    {t.attorney_review_required ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded status-attorney" data-testid={`attorney-flag-${t.id}`}>
                        <Gavel className="w-3 h-3"/>Required
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(t.last_reviewed_date || "").slice(0,10) || "—"}</TableCell>
                  <TableCell className="sticky right-0 bg-card" onClick={(e)=>e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`tracks-actions-${t.id}`} aria-label="Track actions"><MoreHorizontal className="w-4 h-4"/></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[220px]">
                        <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={()=>navigate(`/tracks/${t.id}`)} data-testid={`tracks-action-view-${t.id}`}><Eye className="w-4 h-4 mr-2"/>View</DropdownMenuItem>
                        {writeAccess && (
                          <DropdownMenuItem onClick={()=>navigate(`/tracks/${t.id}`)} data-testid={`tracks-action-edit-${t.id}`}><Pencil className="w-4 h-4 mr-2"/>Edit</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={()=>navigate(`/tracks/${t.id}?tab=compliance`)} data-testid={`tracks-action-review-${t.id}`}><ShieldCheck className="w-4 h-4 mr-2"/>Review</DropdownMenuItem>
                        {(canFlag || writeAccess) && <DropdownMenuSeparator/>}
                        {canFlag && (
                          <DropdownMenuItem onClick={()=>flagAttorney(t)} data-testid={`tracks-action-flag-${t.id}`}><Gavel className="w-4 h-4 mr-2"/>Flag Attorney Review</DropdownMenuItem>
                        )}
                        {writeAccess && (
                          <DropdownMenuItem onClick={()=>navigate(`/submissions/new?track=${t.id}`)} data-testid={`tracks-action-submission-${t.id}`}><Send className="w-4 h-4 mr-2"/>Create Submission Draft</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Disclaimer>Compliance support only · Not legal advice · Human review required before external submission.</Disclaimer>
    </div>
  );
}
